import { ReactNode } from 'react';

import { Box } from '../box/box';
import { useTheme } from '../styles/styles';

const ICONS = {
  // Empty table.
  table: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="M4 9.5h16M9.5 9.5V20" />
    </>
  ),
  // Filtered to nothing.
  search: (
    <>
      <circle cx="11" cy="11" r="6" />
      <path d="m20 20-4.2-4.2" />
    </>
  ),
  // Couldn't load.
  error: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 8v4.5M12 15.8v.2" />
    </>
  ),
  // People (e.g. no employees to show).
  people: (
    <>
      <circle cx="9" cy="9" r="3.2" />
      <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
      <path d="M15.5 6.2a3 3 0 0 1 0 5.6M17.5 14.2a5.5 5.5 0 0 1 3 4.8" />
    </>
  ),
  folder: (
    <path d="M4 7.5A1.5 1.5 0 0 1 5.5 6H10l2 2h6.5A1.5 1.5 0 0 1 20 9.5v8a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 17.5z" />
  ),
};

type GridEmptyStateIcon = keyof typeof ICONS;

export const GridEmptyState = ({
  title,
  hint,
  icon = 'table',
  children,
}: {
  title: string;
  hint?: string;
  icon?: GridEmptyStateIcon;
  children?: ReactNode;
}) => {
  const theme = useTheme();
  return (
    <Box
      role="status"
      sx={{
        height: '100%',
        minHeight: 132,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '4px',
        padding: '20px 16px',
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          mb: '8px',
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          background: theme.custom?.stepBg,
          color: theme.custom?.tableHeaderText,
        }}
      >
        <Box
          component="svg"
          viewBox="0 0 24 24"
          aria-hidden
          sx={{
            width: 20,
            height: 20,
            fill: 'none',
            stroke: 'currentColor',
            strokeWidth: 1.7,
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
          }}
        >
          {ICONS[icon]}
        </Box>
      </Box>
      <Box
        sx={{
          fontSize: '14px',
          fontWeight: 600,
          color: theme.custom?.brandNavy,
        }}
      >
        {title}
      </Box>
      {hint && (
        <Box
          sx={{
            maxWidth: 320,
            fontSize: '12.5px',
            lineHeight: '18px',
            color: theme.custom?.tableHeaderText,
          }}
        >
          {hint}
        </Box>
      )}
      {children && <Box sx={{ mt: '10px' }}>{children}</Box>}
    </Box>
  );
};
